import React, { useEffect, useState } from 'react';
import { Button, Input, Popconfirm, Select, Checkbox, DatePicker, Spin } from 'antd';
import { Table } from 'antd-latest';
import 'antd/dist/antd.css';
import styles from "../../styles.module.scss"
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import classNames from 'classnames';
import { DataSourceHandler } from '@api/data-source-handler';
import { RootState } from '@store/types';
import { useSelector } from 'react-redux';
import { isEmpty, set } from 'lodash';
import { TooltipTop } from '@components/tooltips';
import { ShowWhenTrue } from '@helpers/showwhentrue';
import { BsArrowLeft, BsSearch } from 'react-icons/bs';
import { Flex } from '@components/ui/Flex';
import moment from 'moment';
import { errorHandlerFor400 } from '@api/services/errorhandler';
import { TbVersionsFilled } from 'react-icons/tb';
import { Text } from '@components/ui/Text';
import { MdOutlineUndo, MdRefresh } from 'react-icons/md';
import { HierarchyData } from '../types';
import FilePreviewModal from './FilePreviewModal';
import { width } from 'styled-system';
import { _selectoptionType } from '@components/form/select-field';
import { DeleteConfirm } from '@pages/admin/asset_heirarchy/confirm';
import Spinner from '@components/spinners/spinner';

interface FileContentModalProps {
    content: any[];
    tableColumns: _selectoptionType[];
    hierarchy: HierarchyData;
    onSave: () => void;
    project?: any;
    handleConvertView?: () => void;
    isa95?: boolean;
}

const FileMetaDataTable: React.FC<FileContentModalProps> = ({ content, onSave, hierarchy, tableColumns, project, handleConvertView, isa95 }) => {
    const [editingKeys, setEditingKeys] = useState<string[]>([]);
    const [updatedContent, setUpdatedContent] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(50);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showConfirmRevertToDate, setShowConfirmRevertToDate] = useState(false);
    const [showConfirmRevision, setShowConfirmRevision] = useState(false);
    const [searchText, setSearchText] = useState<string>('');
    const [searchedColumn, setSearchedColumn] = useState<string>('');
    const [revertDate, setRevertDate] = useState<any>(moment());
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [globalSearchText, setGlobalSearchText] = useState<string>('');
    const [hasChanges, setHasChanges] = useState(false);
    const [assetRevisions, setAssetRevisions] = useState<any[]>([]);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [isRevision, setIsRevision] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [recordForRevision, setRecordForRevision] = useState<any>(null);
    const [modelList, setModelList] = useState<any[]>([]);
    const [activeSortColumn, setActiveSortColumn] = useState<string | null>(null);
    const [activeSearchColumn, setActiveSearchColumn] = useState<string | null>(null);
    const [originalContent, setOriginalContent] = useState<any[]>([]);
    const [loadingState, setLoadingState] = useState<{ pull: boolean; push: boolean }>({
        pull: false,
        push: false
    });
    const [filterDropdownVisibleMap, setFilterDropdownVisibleMap] = useState<{ [key: string]: boolean }>({});
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [loadingProperties, setLoadingProperties] = useState<{ [key: string]: boolean }>({});
    const [propertiesData, setPropertiesData] = useState<{ [key: string]: any }>({});
    const workSpaceData = useSelector((store: RootState) => store.ClusterReducer.workspaceList);

    const enabledWorkspace = workSpaceData?.filter((item) => item.isEnabled)?.[0];
    const cluster = useSelector(
        (store: RootState) => store.ClusterReducer.defaultClusterInfo
    );

    const assetRevisionsData = assetRevisions.map((item) => {
        if (project?.type === "Knowledge Graph") {
            const { deleted, revision: rev, ...instanceData } = item.instance;
            return {
                revisionNumber: item.revisionNumber,
                ...instanceData,
                revisionDate: moment(item.revisionDate).format('MM/DD/YYYY HH:mm:ss'),
            }

        } else {
            const { deleted, revision: rev, ...assetData } = item.asset;
            return {
                revisionNumber: item.revisionNumber,
                ...assetData,
                revisionDate: moment(item.revisionDate).format('MM/DD/YYYY HH:mm:ss'),
            }
        }
    })

    const callModelList = () => {
        // DataSourceHandler.getModelList(
        //     `${Env?.REACT_APP_PLATFORM_URL}/asset`,
        //     (response: any) => {
        //         if (response) {
        //             setModelList(isEmpty(response) ? [] : response);
        //         } else {
        //             console.error("Unexpected response format or error", response);
        //             errorAlert("Failed to fetch patch data. Please try again.");
        //         }
        //     }
        // );

        DataSourceHandler.GetNewClasses(
            `${Env?.REACT_APP_PLATFORM_URL}/asset`,
            project?.id,
            (response) => {
                if (response) {
                    setModelList(isEmpty(response) ? [] : response);
                }
            },
            (err: any) => {
                errorAlert("Failed to fetch patch data. Please try again.");
            }
        );
    }

    useEffect(() => {
        callModelList();
    }, [])

    const handlePushPull = (action: "pull" | "push") => {
        if (!isEmpty(enabledWorkspace) && cluster && cluster?.state === "RUNNING" && cluster.defaultCluster) {
            setLoadingState(prev => ({ ...prev, [action]: true }));
            DataSourceHandler.HandlePullPushHierarchy(`${Env?.REACT_APP_PLATFORM_URL}/asset`, hierarchy?.id, action, (response: any) => {
                if (response) {
                    setLoadingState(prev => ({ ...prev, [action]: false }));
                    successAlert(response)
                }
            }, (e: any) => {
                setLoadingState(prev => ({ ...prev, [action]: false }));
                errorAlert(e?.response?.data || "Failed to fetch patch data. Please try again.");
                console.error("Unexpected response format or error", e);
            });
        } else {
            errorAlert("Cluster is not running or workspace is not enabled. Please check the cluster status and workspace status.")
        }
    }

    useEffect(() => {
        const processedContent = content?.map((item, index) => {
            const processedItem = Object.entries(item).reduce((acc: any, [key, value]) => {
                const cleanedKey = key.trim() === '' ? ' ' : key.trim();
                acc[cleanedKey] = value;
                return acc;
            }, {});
            return { ...processedItem, key: index.toString() };
        });

        const draftRows = updatedContent.filter((item) => item?.key?.toString().startsWith('new-'));
        const mergedContent = [...draftRows, ...(processedContent || [])];

        setOriginalContent(mergedContent);
        
        // If there's an active global search, reapply the filter to the new content
        if (globalSearchText) {
            const filteredData = mergedContent.filter((item) =>
                Object.values(item).some((val: any) =>
                    String(val ?? "").toLowerCase().includes(globalSearchText.toLowerCase())
                )
            );
            setUpdatedContent(filteredData);
        } else {
            setUpdatedContent(mergedContent);
        }
    }, [content, globalSearchText]);

    const isEditing = (record: any) => editingKeys.includes(record.key);

    const handleRightClick = (event: MouseEvent) => {
        const modalElement = document.querySelector('.ant-modal');
        if (!modalElement?.contains(event.target as Node)) {
            event.preventDefault();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, key: string, dataIndex: string) => {
        const newData = [...updatedContent];
        const index = newData.findIndex((item) => key === item.key);
        if (index > -1) {
            const item = newData[index];
            newData.splice(index, 1, { ...item, [dataIndex]: e.target.value });
            setUpdatedContent(newData);
            setHasChanges(true);

            const originalIndex = originalContent.findIndex((item) => key === item.key);
            if (originalIndex > -1) {
                const updatedOriginal = [...originalContent];
                updatedOriginal.splice(originalIndex, 1, { ...updatedOriginal[originalIndex], [dataIndex]: e.target.value });
                setOriginalContent(updatedOriginal);
            }
        }
    };

    const getSorter = (a: any, b: any, dataIndex: string) => {
        setActiveSortColumn(dataIndex);
        const valueA = a[dataIndex];
        const valueB = b[dataIndex];

        // Checking if both values can be converted to numbers
        const numA = Number(valueA);
        const numB = Number(valueB);

        // If both values are numbers, performing numeric sorting
        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB; // Ascending order
        }

        // Otherwise, performing string sorting
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
    };

    const hasEmptyColumnTitles = (column: any[]) => {
        return column.slice(1).some(col => col.title.trim() === '');
    };
    // Global Search Handler
    const handleGlobalSearch = (e: React.ChangeEvent<HTMLInputElement>) => {

        const hasEmptyTitle = hasEmptyColumnTitles(generalizedColumns);

        if (hasEmptyTitle) {
            setGlobalSearchText("")
        } else {
            const value = e.target.value;
            setGlobalSearchText(value);

            if (value) {
                const filteredData = originalContent.filter((item) =>
                    Object.values(item).some((val: any) =>
                        String(val ?? "").toLowerCase().includes(value.toLowerCase())
                    )
                );
                setUpdatedContent(filteredData.map((item, index) => ({ ...item, key: index.toString() })));
            } else {
                setUpdatedContent(originalContent.map((item, index) => ({ ...item, key: index.toString() })));
            }
        }
    };

    // Search functionality per column
    const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: string) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setActiveSearchColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setActiveSearchColumn(null)
    };

    const getColumnSearchProps = (dataIndex: string) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ 
                padding: 8, 
                backgroundColor: '#1e293b', 
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 6
            }}>
                <Input
                    placeholder={`Search...`}
                    value={selectedKeys[0]}
                    onChange={(e) => {
                        setSelectedKeys(e.target.value ? [e.target.value] : []);
                        if (e.target.value) {
                            confirm({ closeDropdown: false });
                        }
                    }}
                    onPressEnter={() => confirm()}
                    style={{ 
                        width: 150,
                        backgroundColor: '#334155',
                        border: '1px solid #475569',
                        borderRadius: 4,
                        color: '#e2e8f0',
                        fontSize: 12
                    }}
                    size="small"
                />
                <Button
                    onClick={() => {
                        clearFilters && clearFilters();
                        confirm();
                        setActiveSearchColumn(null);
                    }}
                    size="small"
                    style={{ 
                        backgroundColor: 'transparent',
                        borderColor: '#475569',
                        color: '#e2e8f0',
                        fontSize: 11,
                        padding: '0 8px'
                    }}
                >
                    Reset
                </Button>
            </div>
        ),
        onFilter: (value: any, record: any) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
        filterDropdownVisible: filterDropdownVisibleMap[dataIndex] || false,
        onFilterDropdownVisibleChange: (visible: boolean) => {
            setFilterDropdownVisibleMap((prevState) => ({
                ...prevState,
                [dataIndex]: visible,
            }));
        },
        filterIcon: (filtered: boolean) => (
            <BsSearch 
                style={{ 
                    color: filtered ? '#FCAD01' : '#94a3b8',
                    fontSize: 12
                }} 
            />
        ),
        sorter: (a: any, b: any) => getSorter(a, b, dataIndex),
        render: (text: any) => text,
    });

    const handleSaveAllRows = () => {
        if (hasChanges) {
            const updatedContentWithTag = updatedContent.map(item => ({
                ...item,
                tag: item.tag ?? ""
            }));

            const formattedData = updatedContentWithTag.filter((item) => selectedRowKeys.includes(item.key));
            const formattedDataPayload = formattedData.map(({ key, ...rest }) => rest);
            const isKnowledgeGraphType = project?.type === "Knowledge Graph";
            const allData = updatedContentWithTag.filter(item => !selectedRowKeys.includes(item.key));
            const hasEmptyElements = formattedDataPayload.some(item => (
                isKnowledgeGraphType
                    ? (!item.type || !item.instanceName)
                    : (!item.elementPath || !item.name)
            ));
            if (hasEmptyElements) {
                errorAlert("Empty entry not allowed");
                return;
            }
            const uniqueNames = new Set();
            const uniquePaths = new Set();
            for (const item of formattedDataPayload) {
                if (uniqueNames.has(isKnowledgeGraphType ? item.instanceName : item.name) || (!isKnowledgeGraphType && uniquePaths.has(item.elementPath))) {
                    errorAlert("Only unique entry allowed");
                    return;
                }
                uniqueNames.add(isKnowledgeGraphType ? item.instanceName : item.name);
                !isKnowledgeGraphType && uniquePaths.add(item.elementPath);
            }
            const nameExists = formattedDataPayload.some(newItem =>
                allData.some(existingItem => existingItem.name === newItem.name)
            );
            const pathExists = formattedDataPayload.some(newItem =>
                allData.some(existingItem => existingItem.elementPath === newItem.elementPath)
            );
            // if (nameExists || pathExists) {
            //     nameExists && errorAlert("Name already exists");
            //     pathExists && errorAlert("Element Path  already exists");
            //     return;
            // } else {
            const formatedDataWithid = formattedDataPayload.map((item, index) => {
                // Remove id key if it's an empty string
                if (item.id === '') {
                    const { id, ...rest } = item;
                    return rest;
                }
                return { ...item };
            });

            const jsonData={
                "instances": formatedDataWithid,
                "knowledgeGraphId": hierarchy?.id,
            }
            if (project?.type === "Knowledge Graph") {
                DataSourceHandler.SaveCsvFileMetadataForKnowledgeGraph(`${Env?.REACT_APP_PLATFORM_URL}/asset`, hierarchy?.id, jsonData, (res) => {
                    setEditingKeys([]);
                    setSelectedRowKeys([]);
                    successAlert(res)
                    setHasChanges(false);
                    onSave();
                }, (e: any) => {
                    errorHandlerFor400(e)
                })
            } else {
                DataSourceHandler.SaveCsvFileMetadata(`${Env?.REACT_APP_PLATFORM_URL}/asset`, hierarchy?.id, formatedDataWithid, (res) => {
                    setEditingKeys([]);
                    setSelectedRowKeys([]);
                    successAlert(res)
                    setHasChanges(false);
                    onSave();
                }, (e: any) => {
                    errorHandlerFor400(e)
                })
            }
            // }
        } else {
            errorAlert("No changes to save")
        }
    };

    const handleAddRow = () => {
        const newKey = `new-${Date.now()}`; // Using timestamp to ensure unique key

        // Default columns if content is empty
        const defaultColumns = {
            name: '',
            elementType: '',
            elementCategory: '',
            elementPath: '',
            createdDate: '',
            updatedDate: '',
        };

        // Create empty row based on either existing content structure or default columns
        const emptyRow = content && content[0]
            ? Object.keys(content[0]).reduce((acc: any, key) => {
                acc[key] = '';
                return acc;
            }, { key: newKey })
            : { ...defaultColumns, key: newKey };

        // Add new row at the beginning of the array
        const newData = [emptyRow, ...updatedContent];
        const contentWithKeys = newData.filter(item =>
            item?.key.startsWith('new-')
        ).map((item) => item.key);
        const newOriginalData = [emptyRow, ...originalContent];
        setUpdatedContent(newData);
        setOriginalContent(newOriginalData);
        setEditingKeys(contentWithKeys);
        setSelectedRowKeys(contentWithKeys);
        setCurrentPage(1); // Set to first page
        setHasChanges(true);
    };

    const handleViewRecordVersions = (record: any) => {
        if (project.type === "Knowledge Graph") {
            DataSourceHandler.GetAssetRevisionsForKnowledgeGraph(`${Env?.REACT_APP_PLATFORM_URL}/asset`, record.id, (res) => {
                if (isEmpty(res)) {
                    errorAlert("No revisions found for this record")
                }
                setAssetRevisions(res)
            }, (e: any) => {
                errorAlert(e)
            })
        } else {
            DataSourceHandler.GetAssetRevisions(`${Env?.REACT_APP_PLATFORM_URL}/asset`, record.id, (res) => {
                if (isEmpty(res)) {
                    errorAlert("No revisions found for this record")
                }
                setAssetRevisions(res)
            }, (e: any) => {
                errorAlert(e)
            })
        }
    }

    const handleFetchProperties = (record: any) => {
        if (propertiesData[record.id]) {
            // Properties already loaded, just expand
            return;
        }

        setLoadingProperties(prev => ({ ...prev, [record.id]: true }));

        DataSourceHandler.GetPropertiesByInstanceIdForKnowledgeGraph(
            `${Env?.REACT_APP_PLATFORM_URL}/asset`,
            record.id,
            (res) => {
                setPropertiesData(prev => ({ ...prev, [record.id]: res }));
                setLoadingProperties(prev => ({ ...prev, [record.id]: false }));

                // Update the main content data to include the fetched properties
                setUpdatedContent(prevContent =>
                    prevContent.map(item =>
                        item.id === record.id
                            ? { ...item, entityProperties: res }
                            : item
                    )
                );
            },
            (e: any) => {
                errorAlert(e);
                setLoadingProperties(prev => ({ ...prev, [record.id]: false }));
            }
        );
    };

    const handleDeleteSelectedRows = () => {
        setShowConfirmDelete(false);
        const deletedData = updatedContent.filter((item) => selectedRowKeys.includes(item.key));
        const deletedDataPayload = deletedData.map((item) => item.id);
        const deletedDataPayloadWithIds = deletedDataPayload.filter(Boolean);
        const deletedDataPayloadWithEmptyIds = deletedDataPayload.filter(item => item === "");
        if (!isEmpty(deletedDataPayloadWithEmptyIds)) {
            setUpdatedContent(updatedContent.filter((item) => !selectedRowKeys.includes(item.key)));
            successAlert("Records deleted successfully")
        }
        if (!isEmpty(deletedDataPayloadWithIds)) {
            if (project.type === "Knowledge Graph") {
                DataSourceHandler.DeleteCsvFileMetadataForKnowledgeGraph(`${Env?.REACT_APP_PLATFORM_URL}/asset`, deletedDataPayloadWithIds, (res: any) => {
                    onSave();
                    setEditingKeys(editingKeys.filter((key) => !selectedRowKeys.includes(key)));
                    setSelectedRowKeys([]);
                    successAlert(res)
                    setHasChanges(false);

                }, (e: any) => {
                    errorAlert(e)
                })

            } else {
                DataSourceHandler.DeleteCsvFileMetadata(`${Env?.REACT_APP_PLATFORM_URL}/asset`, hierarchy?.id, deletedDataPayloadWithIds, (res: any) => {
                    onSave();
                    setEditingKeys(editingKeys.filter((key) => !selectedRowKeys.includes(key)));
                    setSelectedRowKeys([]);
                    successAlert(res)
                    setHasChanges(false);

                }, (e: any) => {
                    errorAlert(e)
                })
            }
        }
    };

    const handleRowSelection = (record: any) => {
        const selectedIndex = selectedRowKeys.indexOf(record.key);
        const newSelectedKeys = [...selectedRowKeys];

        if (selectedIndex > -1) {
            newSelectedKeys.splice(selectedIndex, 1);
        } else {
            newSelectedKeys.push(record.key);
        }

        setSelectedRowKeys(newSelectedKeys);
        setEditingKeys(newSelectedKeys);
    };

    const _updatedContent = updatedContent?.map(({ id, ...rest }) => rest)

    const formatColumnName = (key: string) => {
        const fromCamelCase = key.replace(/([A-Z])/g, ' $1');
        const withSpaces = fromCamelCase.replace(/[_-]/g, ' ');
        return withSpaces
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
            .trim();
    };

    const columns = [
        {
            title: '',
            dataIndex: 'selection',
            key: 'selection',
            width: 50,
            render: (_: any, record: any) => (
                <Checkbox
                    checked={selectedRowKeys.includes(record.key)}
                    onChange={() => handleRowSelection(record)}
                    disabled={record.key?.startsWith('new-')}
                />
            )
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            // sorter: (a: any, b: any) => getSorter(a, b, 'name'),
            ...getColumnSearchProps('name'),
            onHeaderCell: () => ({
                className: (activeSortColumn === 'name' || activeSearchColumn === 'name') ? 'highlighted-header' : '',
            }),
            render: (text: any, record: any) => {
                const editable = typeof record.key === "string" && record.key.startsWith("new-") ? isEditing(record) : false;
                return editable ? (
                    <Input
                        value={text}
                        onChange={(e) => handleInputChange(e, record.key, 'name')}
                        onBlur={(e) => {
                            if (e.target.value === '') {
                                handleInputChange({ target: { value: '' } } as any, record.key, 'name');
                            }
                        }}
                    />
                ) : (
                    <div>{text}</div>
                );
            }
        },
        {
            title: 'Element Type',
            dataIndex: 'elementType',
            key: 'elementType',
            width: 150,
            // sorter: (a: any, b: any) => getSorter(a, b, 'elementType'),
            ...getColumnSearchProps('elementType'),
            onHeaderCell: () => ({
                className:
                    activeSortColumn === 'elementType' || activeSearchColumn === 'elementType'
                        ? 'highlighted-header'
                        : '',
            }),
            render: (text: any, record: any) => {
                const editable = isEditing(record);

                if (editable && isa95) {
                    return (
                        <Select
                            value={text || undefined}
                            onChange={(value) =>
                                handleInputChange({ target: { value } } as any, record.key, 'elementType')
                            }
                            style={{ width: '100%' }}
                        >
                            <Select.Option value="Level">Level</Select.Option>
                            <Select.Option value="Resource">Resource</Select.Option>
                            <Select.Option value="Attribute">Attribute</Select.Option>
                        </Select>
                    );
                }

                if (editable) {
                    return (
                        <Select
                            value={text || undefined}
                            onChange={(value) =>
                                handleInputChange({ target: { value } } as any, record.key, 'elementType')
                            }
                            style={{ width: '100%' }}
                        >
                            <Select.Option value="Instance">Instance</Select.Option>
                            <Select.Option value="Attribute">Attribute</Select.Option>
                        </Select>
                    );
                }

                return <div>{text}</div>;
            },
        },
        {
            title: 'Element Category',
            dataIndex: 'elementCategory',
            key: 'elementCategory',
            width: 150,
            // sorter: (a: any, b: any) => getSorter(a, b, 'elementCategory'),
            ...getColumnSearchProps('elementCategory'),
            onHeaderCell: () => ({
                className: (activeSortColumn === 'elementCategory' || activeSearchColumn === 'elementCategory') ? 'highlighted-header' : '',
            }),
            render: (text: any, record: any) => {
                const editable = isEditing(record);
                const isModel = record.elementType === 'Model';
                if (editable && isModel) {
                    return (
                        <Select
                            mode='combobox'
                            value={text || undefined}
                            onChange={(value) => handleInputChange({ target: { value } } as any, record.key, 'elementCategory')}
                            style={{ width: '100%', marginTop: '5px' }}
                        >
                            {modelList.map((model: any) => (
                                <Select.Option key={model.id} value={model.name}>
                                    {model.name}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                }

                return editable ? (
                    <Input
                        value={text}
                        onChange={(e) => handleInputChange(e, record.key, 'elementCategory')}
                    />
                ) : (
                    <div>{text}</div>
                );
            }
        },
        {
            title: isEmpty(project) || project?.type === 'Asset Hierarchy' ? 'Element Path' : 'Property',
            dataIndex: 'elementPath',
            key: 'elementPath',
            width: 200,
            // sorter: (a: any, b: any) => getSorter(a, b, 'elementPath'),
            ...getColumnSearchProps('elementPath'),
            onHeaderCell: () => ({
                className: (activeSortColumn === 'elementPath' || activeSearchColumn === 'elementPath') ? 'highlighted-header' : '',
            }),
            render: (text: any, record: any) => {
                const editable = typeof record.key === "string" && record.key.startsWith("new-") ? isEditing(record) : false;
                return editable ? (
                    <Input
                        value={text}
                        onChange={(e) => handleInputChange(e, record.key, 'elementPath')}
                        onBlur={(e) => {
                            if (e.target.value === '') {
                                handleInputChange({ target: { value: '' } } as any, record.key, 'elementPath');
                            }
                        }}
                    />
                ) : (
                    <div>{text}</div>
                );
            }
        },
        {
            title: 'Value',
            dataIndex: 'tag',
            key: 'tag',
            width: 100,
            // sorter: (a: any, b: any) => getSorter(a, b, 'tag'),
            ...getColumnSearchProps('tag'),
            render: (text: any, record: any) => {
                const editable = isEditing(record);
                const isModel = record.elementType === 'Attribute';

                return editable && isModel ? (
                    <Input
                        value={text}
                        onChange={(e) => handleInputChange(e, record.key, 'tag')}
                    />
                ) : (
                    <div>{text}</div>
                );
            }
        },
        {
            title: 'UOM',
            dataIndex: 'uom',
            key: 'uom',
            width: 150,
            // sorter: (a: any, b: any) => getSorter(a, b, 'uom'),
            ...getColumnSearchProps('uom'),
            onHeaderCell: () => ({
                className: (activeSortColumn === 'uom' || activeSearchColumn === 'uom') ? 'highlighted-header' : '',
            }),
            render: (text: any, record: any) => {
                const editable = isEditing(record);
                return editable ? (
                    <Input
                        value={text}
                        onChange={(e) => handleInputChange(e, record.key, 'uom')}
                        onBlur={(e) => {
                            if (e.target.value === '') {
                                handleInputChange({ target: { value: '' } } as any, record.key, 'uom');
                            }
                        }}
                    />
                ) : (
                    <div>{text}</div>
                );
            }
        },
        {
            title: 'Created Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 150,
            // sorter: (a: any, b: any) => getSorter(a, b, 'createdDate'),
            ...getColumnSearchProps('createdDate'),
            render: (text: any) => text ? moment(text).format('MM/DD/YYYY HH:mm:ss') : ''
        },
        {
            title: 'Updated Date',
            dataIndex: 'updatedDate',
            key: 'updatedDate',
            width: 150,
            // sorter: (a: any, b: any) => getSorter(a, b, 'updatedDate'),
            ...getColumnSearchProps('updatedDate'),
            render: (text: any) => text ? moment(text).format('MM/DD/YYYY HH:mm:ss') : ''
        },
        {
            title: 'Action',
            key: 'operation',
            width: 50,
            render: (record: any) => (
                <TooltipTop title="View this record versions">
                    <button className={styles.versionButton} onClick={() => handleViewRecordVersions(record)}>
                        <div className={styles.center}>
                            <TbVersionsFilled size={"24px"} />
                        </div>
                    </button>
                </TooltipTop>
            ),
        }
    ];

    const generalizedColumns = [
        {
            title: '',
            dataIndex: 'selection',
            key: 'selection',
            width: 50,
            render: (_: any, record: any) => (
                <Checkbox
                    checked={selectedRowKeys.includes(record.key)}
                    onChange={() => handleRowSelection(record)}
                    disabled={record.key?.startsWith('new-')}
                />
            )
        },
        ...tableColumns.map((column) => {
            const existingColumn = columns.find((col) => col.dataIndex === column.value);
            if (existingColumn) {
                return existingColumn;
            }

            return {
                title: column.label,
                dataIndex: column.value,
                key: column.value,
                width: 150,
                // sorter: (a: any, b: any) => getSorter(a, b, column.value),
                ...getColumnSearchProps(column.value),
                onHeaderCell: () => ({
                    className: (activeSortColumn === column.value || activeSearchColumn === column.value) ? 'highlighted-header' : '',
                }),
                render: (text: any, record: any) => {
                    const editable = isEditing(record);
                    return editable && column.value !== "id" ? (
                        <Input
                            value={text}
                            onChange={(e) => handleInputChange(e, record.key, column.value)}
                            onBlur={(e) => {
                                if (e.target.value === '') {
                                    handleInputChange({ target: { value: '' } } as any, record.key, column.value);
                                }
                            }}
                        />
                    ) : (
                        <div>{text}</div>
                    );
                }
            };
        }),
        {
            title: 'Action',
            key: 'operation',
            width: 50,
            render: (record: any) => (
                <TooltipTop title="View this record versions">
                    <button className={styles.versionButton} onClick={() => handleViewRecordVersions(record)}>
                        <div className={styles.center}>
                            <TbVersionsFilled size={"24px"} />
                        </div>
                    </button>
                </TooltipTop>
            ),
        }
    ];

    const handleRevertChangeModal = (record: any) => {
        setSelectedRecord(record);
        setShowConfirmRevision(true);
    };

    const handleRevertChangesPreview = (record: any) => {
        if (project.type === 'Knowledge Graph') {
            DataSourceHandler.RevertAssetRevisionPreviewForKnowledgeGraph(`${Env?.REACT_APP_PLATFORM_URL}/asset`, record.id, record.revisionNumber, hierarchy?.id, (res) => {
                setShowConfirmRevision(false);
                setPreviewData(res)
                setRecordForRevision(record)
                setIsRevision(true);
                setShowPreview(true)
            }, (e: any) => {
                errorAlert(e)
                setShowConfirmRevision(false);
            })
        } else {
            DataSourceHandler.RevertAssetRevisionPreview(`${Env?.REACT_APP_PLATFORM_URL}/asset`, record.id, record.revisionNumber, hierarchy?.id, (res) => {
                setShowConfirmRevision(false);
                setPreviewData(res)
                setRecordForRevision(record)
                setIsRevision(true);
                setShowPreview(true)
            }, (e: any) => {
                errorAlert(e)
                setShowConfirmRevision(false);
            })
        }
    }
    const handleRevertChanges = (record: any) => {
        if (project.type === 'Knowledge Graph') {
            DataSourceHandler.RevertAssetRevisionForKnowledgeGraph(`${Env?.REACT_APP_PLATFORM_URL}/asset`, record.id, record.revisionNumber, (res) => {
                successAlert(res)
                setAssetRevisions([])
                setIsRevision(false)
                onSave();
            }, (e: any) => {
                errorAlert(e)
                setIsRevision(false)
            })
        } else {
            DataSourceHandler.RevertAssetRevision(`${Env?.REACT_APP_PLATFORM_URL}/asset`, record.id, record.revisionNumber, (res) => {
                successAlert(res)
                setAssetRevisions([])
                setIsRevision(false)
                onSave();
            }, (e: any) => {
                errorAlert(e)
                setIsRevision(false)
            })
        }
    }
    const assetRevisionsColumnsForKg = [{
        title: 'Name',
        dataIndex: 'instanceName',
        key: 'instanceName',
        width: 150,
    }, {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 150,
    }, {
        title: 'Created Date',
        dataIndex: 'createdDate',
        key: 'createdDate',
        width: 150,
        render: (text: any) => text ? moment(text).format('MM/DD/YYYY HH:mm:ss') : ''
    },
    {
        title: 'Updated Date',
        dataIndex: 'updatedDate',
        key: 'updatedDate',
        width: 150,
        render: (text: any) => text ? moment(text).format('MM/DD/YYYY HH:mm:ss') : ''
    },
    {
        title: 'Action',
        key: 'operation',
        width: 50,
        render: (record: any) => (
            <>
                <TooltipTop title="Revert to this revision">
                    <button className={styles.versionButton} onClick={() => handleRevertChangeModal(record)}>
                        <div className={styles.center} >
                            <MdOutlineUndo size={"24px"} />
                        </div>
                    </button>
                </TooltipTop>
                <DeleteConfirm
                    onConfirm={() => handleRevertChangesPreview(selectedRecord)}
                    showModal={showConfirmRevision}
                    title={"Are you sure you want to revert to this revision?"}
                    toggleClose={() => {
                        setShowConfirmRevision(false);
                    }}
                />
            </>
        ),
    }
    ];
    const assetRevisionsColumns = [
        ...Object.keys(assetRevisionsData?.[0] || {})
            .filter(key => !['revisionNumber', 'id', 'revisionDate'].includes(key))
            .map((key) => ({
                title: formatColumnName(key),
                dataIndex: key,
                render: (text: any) => {
                    if (['createdDate', 'updatedDate'].includes(key) && text) {
                        return moment(text).format('MM/DD/YYYY HH:mm:ss');
                    }
                    return text;
                },
                key: key,
                width: Math.max(150, key.length * 10),
            })),
        {
            title: 'Action',
            key: 'operation',
            width: 50,
            render: (record: any) => (
                <>
                    <TooltipTop title="Revert to this revision">
                        <button className={styles.versionButton} onClick={() => handleRevertChangeModal(record)}>
                            <div className={styles.center} >
                                <MdOutlineUndo size={"24px"} />
                            </div>
                        </button>
                    </TooltipTop>
                    <DeleteConfirm
                        onConfirm={() => handleRevertChangesPreview(selectedRecord)}
                        showModal={showConfirmRevision}
                        title={"Are you sure you want to revert to this revision?"}
                        toggleClose={() => {
                            setShowConfirmRevision(false);
                        }}
                    />
                </>
            ),
        }
    ];

    const handleDateChange = () => {
        if (project.type === 'Knowledge Graph') {
            DataSourceHandler.RevertAssetTableToDateForKnowledgeGraph(`${Env?.REACT_APP_PLATFORM_URL}/asset`, hierarchy?.id, moment(revertDate).utc().format('MM/DD/YYYY HH:mm:ss'), (res) => {
                successAlert(res)
                onSave();
                setIsRevision(false)
            }, (e: any) => {
                errorHandlerFor400(e)
                setIsRevision(false)
            })
        } else {
            DataSourceHandler.RevertAssetTableToDate(`${Env?.REACT_APP_PLATFORM_URL}/asset`, hierarchy?.id, moment(revertDate).utc().format('MM/DD/YYYY HH:mm:ss'), (res) => {
                successAlert(res)
                onSave();
                setIsRevision(false)
            }, (e: any) => {
                errorHandlerFor400(e)
                setIsRevision(false)
            })
        }
    }
    const handleDateChangePreview = () => {
        if (project.type === 'Knowledge Graph') {
            DataSourceHandler.RevertAssetTableToDatePreviewForKnowledgeGraphPreview(`${Env?.REACT_APP_PLATFORM_URL}/asset`, hierarchy?.id, moment(revertDate).utc().format('MM/DD/YYYY HH:mm:ss'), (res) => {
                setShowConfirmRevertToDate(false);
                setPreviewData(res)
                setIsRevision(false);
                setShowPreview(true);
            }, (e: any) => {
                errorHandlerFor400(e);
                setShowConfirmRevertToDate(false);
            })
        } else {
            DataSourceHandler.RevertAssetTableToDatePreview(`${Env?.REACT_APP_PLATFORM_URL}/asset`, hierarchy?.id, moment(revertDate).utc().format('MM/DD/YYYY HH:mm:ss'), (res) => {
                setShowConfirmRevertToDate(false);
                setPreviewData(res)
                setIsRevision(false);
                setShowPreview(true);
            }, (e: any) => {
                errorHandlerFor400(e);
                setShowConfirmRevertToDate(false);
            })
        }
    }
    const handleClosePreview = () => {
        setIsRevision(false);
        setShowPreview(false);
        setPreviewData([]);
    }

    const handleSavePreview = () => {
        if (isRevision) {
            handleRevertChanges(recordForRevision)
        } else {
            handleDateChange()
        }
        setShowPreview(false);
        setIsRevision(false);
        setTimeout(() => setPreviewData([]), 1000);
    }

    // Nested table search and sort props generator
    const getNestedColumnProps = (dataIndex: string, title: string) => ({
        title,
        dataIndex,
        key: dataIndex,
        sorter: (a: any, b: any) => {
            const valueA = a[dataIndex] ?? '';
            const valueB = b[dataIndex] ?? '';
            return String(valueA).localeCompare(String(valueB));
        },
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ 
                padding: 8, 
                backgroundColor: '#1e293b', 
                borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 6
            }}>
                <Input
                    placeholder={`Search...`}
                    value={selectedKeys[0]}
                    onChange={(e) => {
                        setSelectedKeys(e.target.value ? [e.target.value] : []);
                        if (e.target.value) {
                            confirm({ closeDropdown: false });
                        }
                    }}
                    onPressEnter={() => confirm()}
                    style={{ 
                        width: 130,
                        backgroundColor: '#334155',
                        border: '1px solid #475569',
                        borderRadius: 4,
                        color: '#e2e8f0',
                        fontSize: 12
                    }}
                    size="small"
                />
                <Button
                    onClick={() => {
                        clearFilters && clearFilters();
                        confirm();
                    }}
                    size="small"
                    style={{ 
                        backgroundColor: 'transparent',
                        borderColor: '#475569',
                        color: '#e2e8f0',
                        fontSize: 11,
                        padding: '0 8px'
                    }}
                >
                    Reset
                </Button>
            </div>
        ),
        onFilter: (value: any, record: any) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : false,
        filterIcon: (filtered: boolean) => (
            <BsSearch 
                style={{ 
                    color: filtered ? '#FCAD01' : '#94a3b8',
                    fontSize: 11
                }} 
            />
        ),
    });

    const renderExpandedContent = (record: any) => {
        const { entityProperties } = record;

        if (!entityProperties) {
            return (
                <Text fontStyle="italic" color="#8C8CA1" textAlign="center" padding="20px">
                    No properties available for this record
                </Text>
            );
        }

        const { basicProperties = [], instanceProperties = [], indexedProperties = [] } = entityProperties;
        const hasAnyProperties = basicProperties.length > 0 || instanceProperties.length > 0 || indexedProperties.length > 0;

        if (!hasAnyProperties) {
            return (
                <Text fontStyle="italic" color="#8C8CA1" textAlign="center" padding="20px">
                    No properties available for this record
                </Text>
            );
        }

        // Basic Properties columns with search and sort
        const basicPropertiesColumns = [
            {
                ...getNestedColumnProps('property', 'Property'),
                width: '40%',
                render: (text: string) => (
                    <strong style={{ color: '#E1E1E6' }}>{text}</strong>
                )
            },
            {
                ...getNestedColumnProps('address', 'Address'),
                width: '40%',
                render: (text: string) => (
                    <span style={{ color: '#E1E1E6' }}>{text}</span>
                )
            },
        ];

        // Indexed Properties columns with search and sort
        const indexedPropertiesColumns = [
            {
                ...getNestedColumnProps('property', 'Property'),
                width: '40%',
                render: (text: string) => (
                    <strong style={{ color: '#E1E1E6' }}>{text}</strong>
                )
            },
            {
                ...getNestedColumnProps('address', 'Address'),
                width: '40%',
                render: (text: string) => (
                    <span style={{ color: '#E1E1E6' }}>{text}</span>
                )
            },
        ];

        // Instance Properties columns with search and sort
        const instancePropertiesColumns = [
            {
                ...getNestedColumnProps('property', 'Property'),
                width: '35%',
                render: (text: string) => (
                    <strong style={{ color: '#E1E1E6' }}>{text}</strong>
                )
            },
            {
                ...getNestedColumnProps('targetInstanceName', 'Value'),
                width: '35%',
                render: (text: string) => (
                    <span style={{ color: '#E1E1E6' }}>{text}</span>
                )
            },
            {
                ...getNestedColumnProps('targetInstanceType', 'Type'),
                width: '30%',
                render: (text: string) => (
                    <span style={{ color: '#E1E1E6' }}>{text}</span>
                )
            }
        ];

        return (
            <div style={{ padding: '16px', backgroundColor: '#1a1f36' }}>
                {/* Basic Properties */}
                {basicProperties.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ color: '#E1E1E6', marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                            Basic Properties ({basicProperties.length})
                        </h4>
                        <Table
                            size="small"
                            pagination={basicProperties.length > 10 ? { pageSize: 10, size: 'small' } : false}
                            dataSource={basicProperties.map((prop: any, index: number) => ({
                                key: `basic-${record.key}-${index}`,
                                property: prop.property,
                                address: prop.address,
                                type: prop.type
                            }))}
                            columns={basicPropertiesColumns}
                            style={{ marginBottom: '16px' }}
                            className={styles.nestedTable}
                        />
                    </div>
                )}

                {/* Indexed Properties */}
                {indexedProperties.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ color: '#E1E1E6', marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                            Indexed Properties ({indexedProperties.length})
                        </h4>
                        <Table
                            size="small"
                            pagination={indexedProperties.length > 10 ? { pageSize: 10, size: 'small' } : false}
                            dataSource={indexedProperties.map((prop: any, index: number) => ({
                                key: `indexed-${record.key}-${index}`,
                                property: prop.property,
                                address: prop.address,
                                type: prop.type
                            }))}
                            columns={indexedPropertiesColumns}
                            style={{ marginBottom: '16px' }}
                            className={styles.nestedTable}
                        />
                    </div>
                )}

                {/* Instance Properties */}
                {instanceProperties.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <h4 style={{ color: '#E1E1E6', marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                            Instance Properties ({instanceProperties.length})
                        </h4>
                        <Table
                            size="small"
                            pagination={instanceProperties.length > 10 ? { pageSize: 10, size: 'small' } : false}
                            dataSource={instanceProperties.map((prop: any, index: number) => ({
                                key: `instance-${record.key}-${index}`,
                                property: prop.property,
                                targetInstanceName: prop.targetInstanceName,
                                targetInstanceType: prop.targetInstanceType
                            }))}
                            columns={instancePropertiesColumns}
                            style={{ marginBottom: '16px' }}
                            className={styles.nestedTable}
                        />
                    </div>
                )}
            </div>
        );
    }


    return (
        <div className={styles.fileMetaDataContainer}>
            <ShowWhenTrue show={isEmpty(assetRevisions)}>
                <div className={styles.actionPanel}>
                    <div className={styles.leftActions}>

                        <button
                            className={classNames(
                                "btn-lg btn-yellow-transparent",
                                styles["center"],
                                styles["btn-lg"]
                            )}
                            onClick={handleAddRow}
                        >
                            <img src="/icons/workflow/add.svg" alt="" />
                            <span> Record </span>
                        </button>
                        {selectedRowKeys.length > 0 && (
                            <>

                                <button
                                    className={classNames(
                                        "btn-lg btn-yellow-transparent",
                                        styles["center"],
                                        styles["btn-lg"]
                                    )}
                                    onClick={handleSaveAllRows}
                                >
                                    <span> Save Selected </span>
                                </button>
                                <DeleteConfirm
                                    onConfirm={handleDeleteSelectedRows}
                                    showModal={showConfirmDelete}
                                    title={"Are you sure you want to delete these rows?"}
                                    toggleClose={() => {
                                        setShowConfirmDelete(false);
                                    }}
                                />
                                <button
                                    className={classNames(
                                        "btn-lg btn-red",
                                        styles["center"],
                                        styles["btn-lg"]
                                    )}
                                    onClick={() => setShowConfirmDelete(true)}
                                >
                                    <span> Delete Selected </span>
                                </button>
                            </>
                        )}
                        <ShowWhenTrue show={project.type != "Knowledge Graph"}>
                            <button
                                className={classNames(
                                    styles["pullPushButton"],
                                    styles["center"],
                                    "btn btn-grey btn-refresh-component"
                                )}
                                onClick={() => handlePushPull('pull')}
                                disabled={loadingState.pull}
                            >
                                {loadingState.pull ? <Spin size="default" /> : <span>Pull</span>}
                            </button>
                            <button
                                className={classNames(
                                    styles["pullPushButton"],
                                    styles["center"],
                                    "btn btn-grey btn-refresh-component"
                                )}
                                onClick={() => handlePushPull('push')}
                                disabled={loadingState.push}
                            >
                                {loadingState.push ? <Spin size="default" /> : <span>Push</span>}
                            </button>
                        </ShowWhenTrue>
                        <button
                            className={classNames(
                                "btn btnWt btn-grey btn-refresh-component",
                            )}
                            type="button"
                            onClick={onSave}
                        >
                            <Flex alignItems={"center"} gap="6px">  <MdRefresh />Refresh Data</Flex>
                        </button>
                    </div>
                    <div className={styles.rightActions}>
                        <Flex flexDirection={"row"} alignItems={"center"} gap="10px">
                            <Text fontSize={"14px"} fontWeight={"400"} width={"60px"}>Revert to </Text>
                            <DatePicker showTime format={"MM/DD/YYYY HH:mm:ss"} value={revertDate} onChange={(date) => setRevertDate(date)} />
                            <button className={classNames(
                                "btn-md btn-red",
                                styles["center"],
                            )}
                                onClick={() => setShowConfirmRevertToDate(true)}
                            >Apply</button>
                            <DeleteConfirm
                                onConfirm={() => handleDateChangePreview()}
                                showModal={showConfirmRevertToDate}
                                title={"Are you sure you want to revert to this date?"}
                                toggleClose={() => {
                                    setShowConfirmRevertToDate(false);
                                }}
                            />
                            <Input
                                placeholder="Search..."
                                style={{ width: 300 }}
                                value={globalSearchText}
                                onChange={handleGlobalSearch}
                                className={styles.searchInput}
                            />
                            {/* {project?.type === 'Asset Hierarchy' ? <TooltipTop title="Asset Hierarchy"><img onClick={() => handleConvertView?.()} style={{ cursor: "pointer" }} src="/icons/asset-hierarchy.svg" alt="" /></TooltipTop> : <TooltipTop title="Knowledge Graph"><img style={{ cursor: "pointer" }} onClick={() => handleConvertView?.()} src="/icons/knowledge-graph.svg" alt="" width={24} /></TooltipTop>} */}
                        </Flex>
                    </div>
                </div>
                <div className={styles.tableWrapper}>
                    <Table
                        columns={generalizedColumns}
                        dataSource={updatedContent}
                        size='small'
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: updatedContent?.length,
                            onChange: (page, size) => {
                                setCurrentPage(page);
                                if (size && size !== pageSize) setPageSize(size);
                            },
                            showSizeChanger: true,
                            onShowSizeChange: (_current, size) => {
                                setPageSize(size);
                                setCurrentPage(1);
                            },
                            pageSizeOptions: ['10', '50', '100', '250'],
                            className: styles.tablePagination
                        }}
                        className={styles.dataTable}
                        rowClassName={(record) =>
                            `${styles.tableRow} ${selectedRowKeys.includes(record.key) ? styles.selectedRow : ''}`
                        }
                        expandable={{
                            expandedRowKeys,
                            onExpand: (expanded, record) => {
                                if (expanded && record.hasProperties && !propertiesData[record.id]) {
                                    handleFetchProperties(record);
                                }

                                const keys = expanded
                                    ? [...expandedRowKeys, record.key]
                                    : expandedRowKeys.filter(key => key !== record.key);
                                setExpandedRowKeys(keys);
                            },
                            expandedRowRender: (record) => {
                                if (loadingProperties[record.id]) {
                                    return (
                                        <div style={{ padding: '20px', textAlign: 'center' }}>
                                            <Spin size="small" /> Loading properties...
                                        </div>
                                    );
                                }

                                const properties = propertiesData[record.id] || record.entityProperties;
                                if (properties) {
                                    return renderExpandedContent({ ...record, entityProperties: properties });
                                }

                                return (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                        No properties available
                                    </div>
                                );
                            },
                            rowExpandable: (record) => !!(record.hasProperties),
                            expandIcon: ({ expanded, onExpand, record }) =>
                                (record.hasProperties) ? (
                                    <span
                                        className={`ant-table-row-expand-icon ${expanded ? 'ant-table-row-expand-icon-expanded' : 'ant-table-row-expand-icon-collapsed'
                                            }`}
                                        onClick={e => onExpand(record, e)}
                                        style={{
                                            opacity: loadingProperties[record.id] ? 0.5 : 1,
                                            cursor: loadingProperties[record.id] ? 'wait' : 'pointer'
                                        }}
                                    />
                                ) : null
                        }}
                        locale={{
                            emptyText: (
                                <div className={styles.emptyState}>
                                    {updatedContent?.length === 0
                                        ? 'No Data Available'
                                        : 'No matching results found'}
                                </div>
                            )
                        }}
                    />
                </div>
            </ShowWhenTrue>
            <FilePreviewModal isOpen={showPreview} project={project} onClose={() => handleClosePreview()} data={previewData} isRevisions={isRevision} onSave={() => handleSavePreview()} />
            <ShowWhenTrue show={!isEmpty(assetRevisions)}>
                <div className={styles.assetRevisionsContainer}>

                    <Flex flexDirection={"row"} alignItems={"center"} style={{ cursor: "pointer", marginBottom: "20px" }} gap="10px" onClick={() => setAssetRevisions([])}><BsArrowLeft />  <h3 className="white"> Revisions </h3>   </Flex>
                    <Table
                        columns={project?.type === "Knowledge Graph" ? assetRevisionsColumnsForKg : assetRevisionsColumns}
                        size='small'
                        dataSource={assetRevisionsData}
                        className={styles.dataTableRevert}
                    />
                </div>
            </ShowWhenTrue>
        </div>
    );
};

export default FileMetaDataTable;